import React, { useEffect, useRef, useState } from 'react';
import { Paper, PaperProps } from '@mui/material';
import Popper, { PopperProps } from '@mui/material/Popper';
import { KeyValue, KeyValueDefault } from '@/main/models/common';
import { Autocomplete } from '@mui/material';
import { KeyboardArrowDown } from '@mui/icons-material';
import {
  Controller,
  FieldError,
  RegisterOptions,
  UseFormSetValue
} from 'react-hook-form';
import { Spinner } from './styles';
import KogInputTextOutlined from '../inputText/inputTextOutlined';
import {
  getTranslateByKey,
  isEmpty,
  isKeyValueDefault,
  isNullUndefined
} from '@/main/services/utils';
import { uppercaseFirstLetter } from '@/main/services/utilsString';
import { useStylesOutlined } from './stylesOutlined';

type IAutoCompleteOutlinedProps = {
  id?: string;
  label?: string;
  control: any;
  isLoading?: boolean;
  options: (KeyValue | KeyValueDefault)[] | null;
  onChangeCustom: (valueSelected: KeyValue | KeyValueDefault | null) => void;
  hasErrorCustom?: boolean;
  errorMessageCustom?: string;
  rulesCustom?: { required: boolean } & RegisterOptions;
  nameCustom: string;
  autoHighlightCustom?: boolean;
  setValueCustom?: UseFormSetValue<any>;
  valueDefaultKey?: string | number | null;
  isDefaultValueUnique?: boolean;
  disabledMessage?: boolean;
  autoOnChangeCustom?: boolean;
  hideValue?: boolean;
  isPluralLabel?: boolean;
  disableClearable?: boolean;
  disablePortal?: boolean;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  style?: React.CSSProperties;
  onBlur?: () => void;
};

const CustomPaper = (props: PaperProps) => {
  return (
    <Paper
      elevation={8}
      {...props}
      className={`${props.className} ignore-click`}
    />
  );
};

const CustomPopper = (props: PopperProps) => (
  <Popper
    {...props}
    style={{ ...props.style, width: 'auto', minWidth: props.style?.width as string }}
  />
);

function KogAutoCompleteOutlinedControlled({
  id,
  label,
  control,
  isLoading,
  options,
  onChangeCustom,
  hasErrorCustom,
  errorMessageCustom,
  nameCustom,
  rulesCustom = { required: false },
  autoHighlightCustom,
  setValueCustom,
  valueDefaultKey,
  isDefaultValueUnique = false,
  disabledMessage = false,
  hideValue = false,
  autoOnChangeCustom = false,
  isPluralLabel = false,
  disabled,
  required,
  className,
  style,
  onBlur,
  placeholder,
  disableClearable,
  disablePortal,
}: IAutoCompleteOutlinedProps) {
  const classes = useStylesOutlined();
  const getOptions = () => (options !== null ? options : []);
  const optionsDrop = getOptions();
  const renderRef = useRef(false);
  const [openAutocomplete, setOpenAutocomplete] = useState(false);

  const renderErrorMessages = (error: FieldError | undefined) => {
    const requiredMessage = getTranslateByKey(
      !isNullUndefined(isPluralLabel) && isPluralLabel
        ? 'information.areRequired'
        : 'information.isRequired',
      true
    );
    const fieldMessage = getTranslateByKey('information.field');

    if (!error) return null;
    switch (error.type) {
      case 'required':
        return uppercaseFirstLetter(
          requiredMessage.replace('{0}', `${label || fieldMessage}`)
        );
      default:
        return error.message;
    }
  };

  // UseEffect's
  useEffect(() => {
    if (autoHighlightCustom && setValueCustom && optionsDrop.length > 0) {
      const firstOption = optionsDrop[0];
      setValueCustom(nameCustom, firstOption);
    }
  }, [autoHighlightCustom]);

  useEffect(() => {
    if (
      !isLoading &&
      optionsDrop.length > 0 &&
      !isEmpty(valueDefaultKey) &&
      setValueCustom &&
      !renderRef.current
    ) {
      const findValue = optionsDrop.find(option => isKeyValueDefault(option) ? (String(option.default) === String(valueDefaultKey) || option.default === 'True') : String(option.key) === String(valueDefaultKey));
      setValueCustom(nameCustom, findValue);
      renderRef.current = true;
    }
  }, [isLoading, valueDefaultKey, JSON.stringify(optionsDrop)]);

  useEffect(() => {
    if (optionsDrop.length === 1 && setValueCustom && isDefaultValueUnique) {
      setValueCustom(nameCustom, optionsDrop[0]);
      if (autoOnChangeCustom) {
        onChangeCustom(optionsDrop[0]);
      }
    }
  }, [JSON.stringify(optionsDrop), isDefaultValueUnique]);

  return (
    <Controller
      control={control}
      name={nameCustom}
      rules={rulesCustom}
      render={({ field: { onChange, value }, fieldState }) => {
        return (
          <Autocomplete
            id={id}
            open={openAutocomplete}
            popupIcon={isLoading ? <Spinner /> : <KeyboardArrowDown />}
            className={`${classes.inputAutoCompleteOutlined} ignore-click ${className || ''}`}
            style={style}
            size="small"
            loading={isLoading}
            disabled={isLoading || disabled}
            disableClearable={disableClearable}
            disablePortal={disablePortal}
            onChange={(
              _,
              optionSelected
            ) => {
              onChange(optionSelected);
              onChangeCustom(optionSelected as KeyValue | KeyValueDefault | null);
            }}
            onInputChange={(
              _: React.ChangeEvent<{}>,
              newInputValue: string,
              reason: string
            ) => {
              if (reason === 'clear') setOpenAutocomplete(false);
              if (newInputValue === '') setOpenAutocomplete(false);
            }}
            PaperComponent={CustomPaper}
            PopperComponent={CustomPopper}
            value={hideValue ? null : value}
            options={getOptions()}
            getOptionKey={(option: KeyValue | KeyValueDefault) =>
              isKeyValueDefault(option) ? String(option.default) : String(option.key)
            }
            getOptionLabel={(option: KeyValue | KeyValueDefault) =>
              option?.value || ''
            }
            renderInput={params => (
              <KogInputTextOutlined
                {...params.InputProps}
                inputProps={params.inputProps}
                placeholder={placeholder}
                error={!!hasErrorCustom || !!fieldState.error}
                required={Boolean(rulesCustom?.required) || required}
                disabled={disabled}
              />
            )}
            onOpen={() => setOpenAutocomplete(true)}
            onClose={() => setOpenAutocomplete(false)}
            onBlur={onBlur}
          />
        );
      }}
    />
  );
}

export default KogAutoCompleteOutlinedControlled;
